import { useMemo, useState } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { useAvailability, useFacility, useUnclearedAdjustments } from './api'
import {
  CashflowV2Error,
  CashflowV2Loading,
  CashflowV2Unconfigured,
  looksUnconfigured,
} from './components/PageStates'
import { colors, fmt } from './utils'
import type {
  AvailabilitySummaryRow,
  FacilityConfig,
  UnclearedAdjustment,
} from './types'

function KpiCard({
  label,
  value,
  subtext,
  color,
}: {
  label: string
  value: string
  subtext?: string
  color?: string
}) {
  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-5 flex flex-col gap-1 min-w-0 dark:bg-dark-surface dark:border-dark-border">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </span>
      <span
        className="text-2xl font-semibold truncate text-gray-900 dark:text-gray-100"
        style={color ? { color } : undefined}
      >
        {value}
      </span>
      {subtext && (
        <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
          {subtext}
        </span>
      )}
    </div>
  )
}

function headroomColor(headroom: number, covenantFloor: number | undefined) {
  if (covenantFloor != null && covenantFloor > 0) {
    const ratio = headroom / covenantFloor
    if (ratio >= 0.15) return colors.status.green
    if (ratio >= 0.05) return colors.status.yellow
    return colors.status.red
  }
  return headroom >= 0 ? colors.status.green : colors.status.red
}

interface WaterfallStep {
  name: string
  base: number
  value: number
  displayAmount: number
  fill: string
  labelFill: string
}

function buildWaterfall(row: AvailabilitySummaryRow): WaterfallStep[] {
  const book = row.book_cash
  const bankDelta = row.bank_cash - row.book_cash
  let cumulative = book

  const steps: WaterfallStep[] = []

  steps.push({
    name: 'Book Cash',
    base: 0,
    value: book,
    displayAmount: book,
    fill: colors.accent.teal,
    labelFill: colors.text.primary,
  })
  cumulative += bankDelta

  steps.push({
    name: 'Bank Delta',
    base: book,
    value: bankDelta,
    displayAmount: bankDelta,
    fill: colors.accent.teal,
    labelFill: bankDelta >= 0 ? colors.text.primary : '#ffffff',
  })

  const restricted = row.restricted_cash
  steps.push({
    name: 'Restricted',
    base: cumulative - restricted,
    value: restricted,
    displayAmount: -restricted,
    fill: colors.status.red,
    labelFill: '#ffffff',
  })
  cumulative -= restricted

  const checks = row.outstanding_checks
  steps.push({
    name: 'Outstanding Checks',
    base: cumulative - checks,
    value: checks,
    displayAmount: -checks,
    fill: colors.status.red,
    labelFill: '#ffffff',
  })
  cumulative -= checks

  const deposits = row.uncleared_deposits
  steps.push({
    name: 'Uncleared Deposits',
    base: cumulative,
    value: deposits,
    displayAmount: deposits,
    fill: colors.status.green,
    labelFill: colors.text.primary,
  })
  cumulative += deposits

  const floatAmount = row.other_float
  steps.push({
    name: 'Float',
    base: cumulative,
    value: floatAmount,
    displayAmount: floatAmount,
    fill: colors.status.green,
    labelFill: colors.text.primary,
  })
  cumulative += floatAmount

  const trueOperating = row.true_operating_cash
  const trueOperatingBridge = trueOperating - cumulative
  steps.push({
    name: 'True Op Cash',
    base: trueOperatingBridge >= 0 ? cumulative : trueOperating,
    value: Math.abs(trueOperatingBridge),
    displayAmount: trueOperatingBridge === 0 ? trueOperating : trueOperatingBridge,
    fill:
      trueOperatingBridge === 0
        ? colors.accent.teal
        : trueOperatingBridge > 0
          ? colors.status.green
          : colors.status.red,
    labelFill: trueOperatingBridge < 0 ? '#ffffff' : colors.text.primary,
  })
  cumulative = trueOperating

  const credit = row.available_credit
  steps.push({
    name: 'Credit',
    base: cumulative,
    value: credit,
    displayAmount: credit,
    fill: colors.accent.gold,
    labelFill: colors.text.primary,
  })
  cumulative += credit

  const totalAvailability = row.total_availability
  const totalBridge = totalAvailability - cumulative
  steps.push({
    name: 'Total Availability',
    base: totalBridge >= 0 ? cumulative : totalAvailability,
    value: Math.abs(totalBridge),
    displayAmount: totalBridge === 0 ? totalAvailability : totalBridge,
    fill: colors.accent.indigo,
    labelFill: totalBridge < 0 ? '#ffffff' : colors.text.primary,
  })

  return steps
}

function WaterfallLabel(props: {
  x?: number
  y?: number
  width?: number
  height?: number
  payload?: WaterfallStep
}) {
  const { x, y, width, height, payload } = props
  if (x == null || y == null || width == null || !payload) return null
  const cy = height && height < 0 ? y : y + (height ?? 0) / 2
  return (
    <text
      x={x + width / 2}
      y={cy}
      fill={payload.labelFill}
      fontSize={11}
      fontWeight={600}
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {fmt.compact(payload.displayAmount)}
    </text>
  )
}

export default function CashTruthPage() {
  const availability = useAvailability()
  const uncleared = useUnclearedAdjustments()
  const facility = useFacility()

  const [selectedWeek, setSelectedWeek] = useState(0)

  const sortedRows: AvailabilitySummaryRow[] = useMemo(() => {
    const rows = availability.data ?? []
    return [...rows].sort((a, b) => Number(a.week) - Number(b.week))
  }, [availability.data])

  const weekCount = sortedRows.length
  const safeWeekIndex =
    weekCount > 0 ? Math.min(Math.max(selectedWeek, 0), weekCount - 1) : 0
  const selectedRow = sortedRows[safeWeekIndex]

  const fac: FacilityConfig | undefined = facility.data
  const unclearedItems: UnclearedAdjustment[] = uncleared.data ?? []

  const waterfallData = useMemo(
    () => (selectedRow ? buildWaterfall(selectedRow) : []),
    [selectedRow],
  )

  const trendData = useMemo(
    () =>
      sortedRows.map(row => ({
        week: row.week,
        label: `W${row.week}`,
        true_operating_cash: row.true_operating_cash,
        total_availability: row.total_availability,
      })),
    [sortedRows],
  )

  const covenantRef = fac?.minimum_cash_covenant

  const isLoading = availability.isLoading || uncleared.isLoading || facility.isLoading
  const firstError = availability.error || uncleared.error || facility.error

  if (isLoading) {
    return <CashflowV2Loading label="Loading cash truth…" />
  }

  if (firstError) {
    if (looksUnconfigured(firstError)) {
      return <CashflowV2Unconfigured />
    }
    return (
      <CashflowV2Error
        title="Failed to load cash truth data"
        description={(firstError as Error).message}
        onRetry={() => {
          availability.refetch()
          uncleared.refetch()
          facility.refetch()
        }}
      />
    )
  }

  const headroom = selectedRow?.covenant_headroom ?? 0
  const headroomClr = headroomColor(headroom, covenantRef)

  return (
    <div className="space-y-6 pb-12">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Cash Truth
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Book-to-true-cash bridge and availability analysis.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="True Op Cash"
          value={selectedRow ? fmt.compact(selectedRow.true_operating_cash) : '—'}
          subtext={selectedRow ? fmt.weekLabel(selectedRow.week_ending) : undefined}
          color={colors.accent.teal}
        />
        <KpiCard
          label="Total Availability"
          value={selectedRow ? fmt.compact(selectedRow.total_availability) : '—'}
          color={colors.accent.gold}
        />
        <KpiCard
          label="Covenant Headroom"
          value={selectedRow ? fmt.compact(selectedRow.covenant_headroom) : '—'}
          subtext={covenantRef != null ? `Floor ${fmt.compact(covenantRef)}` : undefined}
          color={headroomClr}
        />
        <KpiCard
          label="Available Credit"
          value={selectedRow ? fmt.compact(selectedRow.available_credit) : '—'}
          subtext={fac?.facility_name}
          color={colors.accent.indigo}
        />
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-gray-100 dark:bg-dark-surface dark:border-dark-border p-4">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Forecast week
        </span>
        <div className="mt-3 flex flex-wrap gap-2">
          {Array.from({ length: 13 }, (_, i) => {
            const weekNum = i + 1
            const idx = sortedRows.findIndex(row => Number(row.week) === weekNum)
            const row = idx >= 0 ? sortedRows[idx] : undefined
            const active = !!selectedRow && Number(selectedRow.week) === weekNum
            return (
              <button
                key={weekNum}
                type="button"
                disabled={!row}
                onClick={() => idx >= 0 && setSelectedWeek(idx)}
                className={`min-w-[2.75rem] rounded-lg px-3 py-2 text-sm font-semibold transition-colors border ${
                  active
                    ? 'bg-teal-500 text-white border-teal-500 shadow-sm'
                    : 'bg-gray-50 dark:bg-dark-surface-2 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-dark-border hover:bg-gray-100 dark:hover:bg-dark-border disabled:opacity-40 disabled:cursor-not-allowed'
                }`}
              >
                W{weekNum}
              </button>
            )
          })}
        </div>
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-gray-100 dark:bg-dark-surface dark:border-dark-border p-5">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4">
          Cash Waterfall (selected week)
        </h2>
        {!selectedRow ? (
          <p className="text-sm text-gray-400">No availability data for this week.</p>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={waterfallData} margin={{ top: 24, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={colors.chart.gridLine}
                vertical={false}
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10, fill: colors.text.secondary }}
                interval={0}
                angle={-18}
                textAnchor="end"
                height={72}
              />
              <YAxis
                tickFormatter={(value: number) => fmt.compact(value)}
                tick={{ fontSize: 11, fill: colors.text.secondary }}
                width={76}
              />
              <Tooltip
                formatter={(
                  _value: unknown,
                  _name: unknown,
                  item: { payload?: WaterfallStep },
                ) => {
                  const payload = item.payload as WaterfallStep
                  return [fmt.currency(payload.displayAmount), payload.name]
                }}
                contentStyle={{
                  borderRadius: 8,
                  fontSize: 12,
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,.1)',
                }}
              />
              <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />
              <Bar
                dataKey="value"
                stackId="wf"
                radius={[4, 4, 0, 0]}
                maxBarSize={56}
                isAnimationActive={false}
              >
                {waterfallData.map((entry, index) => (
                  <Cell key={`${entry.name}-${index}`} fill={entry.fill} />
                ))}
                <LabelList dataKey="value" content={<WaterfallLabel />} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-gray-100 dark:bg-dark-surface dark:border-dark-border p-5">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4">
          13-Week Availability Trend
        </h2>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={trendData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
            <defs>
              <linearGradient id="cashTruthTeal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.accent.teal} stopOpacity={0.35} />
                <stop offset="100%" stopColor={colors.accent.teal} stopOpacity={0.04} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={colors.chart.gridLine}
              vertical={false}
            />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: colors.text.secondary }} />
            <YAxis
              tickFormatter={(value: number) => fmt.compact(value)}
              tick={{ fontSize: 12, fill: colors.text.secondary }}
              width={76}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                fmt.currency(value),
                name === 'true_operating_cash' ? 'True Op Cash' : 'Total Availability',
              ]}
              labelFormatter={label => `${label}`}
              contentStyle={{
                borderRadius: 8,
                fontSize: 12,
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,.1)',
              }}
            />
            {covenantRef != null && (
              <ReferenceLine
                y={covenantRef}
                stroke={colors.status.red}
                strokeDasharray="6 4"
                strokeWidth={2}
                label={{
                  value: 'Covenant',
                  position: 'insideTopRight',
                  fill: colors.status.red,
                  fontSize: 11,
                }}
              />
            )}
            <Area
              type="monotone"
              dataKey="true_operating_cash"
              name="true_operating_cash"
              stroke={colors.accent.teal}
              strokeWidth={2}
              fill="url(#cashTruthTeal)"
            />
            <Area
              type="monotone"
              dataKey="total_availability"
              name="total_availability"
              stroke={colors.accent.gold}
              strokeWidth={2}
              fill="none"
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-gray-100 dark:bg-dark-surface dark:border-dark-border overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-dark-border">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Uncleared Items
          </h2>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Outstanding timing and reconciliation adjustments
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-dark-surface-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Reference</th>
                <th className="px-4 py-3">Payee/Source</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Issue Date</th>
                <th className="px-4 py-3">Expected Clear</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Entity</th>
              </tr>
            </thead>
            <tbody>
              {unclearedItems.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-gray-400 dark:text-gray-500 text-sm"
                  >
                    No uncleared items
                  </td>
                </tr>
              )}
              {unclearedItems.map((item, index) => (
                <tr
                  key={item.item_id}
                  className={
                    index % 2 === 0
                      ? 'bg-white dark:bg-dark-surface'
                      : 'bg-gray-50/80 dark:bg-dark-surface-2/40'
                  }
                >
                  <td className="px-4 py-2.5 text-gray-800 dark:text-gray-100">{item.type}</td>
                  <td className="px-4 py-2.5 text-gray-700 dark:text-gray-200 font-mono text-xs">
                    {item.reference_number}
                  </td>
                  <td
                    className="px-4 py-2.5 text-gray-700 dark:text-gray-200 max-w-[200px] truncate"
                    title={item.payee_or_source}
                  >
                    {item.payee_or_source}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-medium text-gray-900 dark:text-gray-100">
                    {fmt.currency(item.amount)}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {fmt.shortDate(item.issue_date)}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {fmt.shortDate(item.expected_clear_date)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex rounded-full bg-gray-100 dark:bg-dark-surface-2 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-700 dark:text-gray-200">
                    {item.entity}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
